import type { ExportOps } from '@mimir/client-common/fileSync/exportOps';
import type { SequenceInfo } from '@mimir/primitive/premiere/sequenceDetails';
import type { AdobePanelContext, ElectronContext } from '@mimir/common/context/applicationContext';
import type { PremiereSequenceReference } from '@mimir/primitive/premiere/premiereReference';
import type { Logger } from '@mimir/common/types/logger';
import { addEventListener } from './cep';
import { dirname, getBasenameWithoutExtension, isFileAvailableLocally, largestFileWithPrefixInDir } from '@mimir/common/util/fs';
import { invariant } from '@mimir/common/util/invariant';
import isEmpty from 'lodash/isEmpty';
import { showToast, TOAST } from '@mimir/web/eventBus';

import { defer, type PromiseSource } from '@mimir/common/async/async';
import Axios from 'axios';
import { isInAdobeApp, isInElectron } from '@mimir/common/context/applicationContext';
import { getSequenceKey } from '@mimir/primitive/nle/clip';


/**
 * Communicate what's going on with the local Adobe Media Encoder that we
 * delegate rendering to.
 *
 * The events that are emitted by our code in the Adobe application JavaScript
 * context, and consumed here in the browser JavaScript context.
 */
interface EncoderJobEvent {
  jobId: string,

  // When present, signifies successful completion.
  outputFilePath?: string,

  // When present, signifies failure
  errorMessage?: string,

  // When present and true, signifies cancellation
  canceled?: true,

  progress?: number,
}


export class DefaultExportOps implements ExportOps {

  constructor(
    private applicationContext: AdobePanelContext | ElectronContext,
    private logger: Logger,
    private selectedPresetPath: string | undefined,
    private dvrMode: boolean,
  ) {}


  async getSequenceExportInfo(sequenceRef: PremiereSequenceReference, outputPresetPath: string): Promise<SequenceInfo> {
    if (this.dvrMode) {
      invariant(isInElectron(this.applicationContext), 'dvrMode in non-Electron context??');
      const electronContext = this.applicationContext;
      const sequenceExportInfo = await electronContext.cepBridgeClient.call('getSequenceExportInfoWithItemInfo');
      if (outputPresetPath && sequenceExportInfo.exportInfo) {
        const presetDetails = await electronContext.cepBridgeClient.call('loadDvrRenderPreset', outputPresetPath);
        this.logger.info(`presetDetails: ${JSON.stringify(presetDetails)} from ${outputPresetPath}`);
        sequenceExportInfo.exportInfo.exportedFileExtension = presetDetails?.format || '';
      }
      return sequenceExportInfo;
    }

    invariant(isInAdobeApp(this.applicationContext), 'Should not invoke getSequenceExportInfo outside of Adobe panel context');
    const cepb = this.applicationContext.cepBridgeClient;
    const itemInfos = await cepb.call('getItemInfosFromSequence', sequenceRef);

    const sequenceKeyParam = getSequenceKey(sequenceRef.sequenceId, sequenceRef.projectPath);

    outputPresetPath = (outputPresetPath && await isFileAvailableLocally(this.applicationContext.nodeApi, this.logger, outputPresetPath)) ? outputPresetPath : '';
    const exportInfo = await cepb.call('getSequenceExportInfo', sequenceKeyParam, outputPresetPath);

    return {
      exportInfo,
      itemInfos,
    };
  }


  async exportSequenceAsProject(sequenceKey: string, exportedSequenceName: string, exportedProjectName: string, exportDir: string): Promise<void> {
    await this.applicationContext.cepBridgeClient.call('exportSequenceAsProject', sequenceKey, exportedSequenceName, exportedProjectName, exportDir);
  }


  async writePremiereSequenceToFile(
    sequenceKey: string,
    exportedFilePath: string,
    presetPath: string | undefined,
    ameProgressHandler: (progress: number) => void,
  ): Promise<string> {
    if (this.dvrMode && isInElectron(this.applicationContext)) {
      const targetDir = dirname(this.applicationContext.nodeApi, exportedFilePath);
      const targetFileName = getBasenameWithoutExtension(this.applicationContext.nodeApi, exportedFilePath);

      const electronContext = this.applicationContext;
      invariant(presetPath, `Invalid presetPath (${presetPath})`);
      await electronContext.cepBridgeClient.call('exportFromDvr', targetDir, targetFileName, presetPath);

      // exportedFilePath passed above may not have the correct file extension due to DVR API limitation. So
      // we try our best to parse it from the output file available after the rendering is complete. Although
      // DVR does not seem to produce intermediate files like .aac and .m4v, we are still assuming that the
      // correct final output file will be the largest in size, in case multiple files with the same GUID prefix
      // is found.
      const probableOutputFileName = await largestFileWithPrefixInDir(electronContext.nodeApi, targetDir, `${targetFileName}.`);
      if (!probableOutputFileName) {
        throw new Error(`Output file not found in ${targetDir} for ${targetFileName}`);
      }

      const foundOutputFile = this.applicationContext.nodeApi.path.join(targetDir, probableOutputFileName);
      console.log(`Found output: ${foundOutputFile}`);

      return foundOutputFile;
    }

    invariant(isInAdobeApp(this.applicationContext), 'applicationContext should be Adobe panel context');
    const selectedPresetPath = presetPath ?? this.selectedPresetPath;
    const exportResult = await exportSequence(this.logger, this.applicationContext, sequenceKey, exportedFilePath, selectedPresetPath);
    const job = getEncoderJob(exportResult.jobId);
    job.ameProgressHandler = ameProgressHandler;
    return job.getExportedFile();
  }

}


async function exportSequence(logger: Logger, adobeContext: AdobePanelContext, sequenceKey: string, exportedFilePath: string, selectedPresetPath: string | undefined): Promise<{ jobId: string }> {
  if (isEmpty(selectedPresetPath) || !(await isFileAvailableLocally(adobeContext.nodeApi, logger, selectedPresetPath))) {
    logger.error(`exportSequence: preset not found: ${selectedPresetPath}`);
    const msg = `Preset not found ${selectedPresetPath ?? ''}`;
    showToast(msg, TOAST.ERROR);
    throw new Error(msg);
  }

  registerEncoderJobEventHandlers();

  const result = await adobeContext.cepBridgeClient.call('exportSequence', sequenceKey, exportedFilePath, selectedPresetPath ?? '');
  invariant(result.jobId != null, 'Missing Adobe Media Encoder jobId');
  return result as { jobId: string };
}


const registerEncoderJobEventHandlers = () => {
  if (!encoderJobEventHandlersRegistered) {
    console.log('registerEncoderJobEventHandlers once');
    addEventListener('no.mjoll.mimir.premiere.EncoderJobEvent', encoderJobEventHandler);
    encoderJobEventHandlersRegistered = true;
  }
};


function encoderJobEventHandler(event: any) {
  try {
    // weird object sent. must parse after strigifying manually.
    const dataString = JSON.stringify(event.data);
    const dataJson = JSON.parse(dataString);
    const jobEvent: Partial<EncoderJobEvent> = dataJson;
    if (jobEvent.jobId != null) {
      getEncoderJob(jobEvent.jobId).processEvent(jobEvent as EncoderJobEvent);
    }
  } catch (err) {
    console.log(`encoderJobEventHandler: event.data: ${event.data}, err:` + err);
  }
}


function getEncoderJob(jobId: string) {
  let job = lastEncoderEventByJobId.get(jobId);
  if (!job) {
    job = new EncoderJob();
    lastEncoderEventByJobId.set(jobId, job);
  }

  return job;
}


let encoderJobEventHandlersRegistered = false;

/**
 * All ongoing local Adobe Media Encoder jobs.
 */
const lastEncoderEventByJobId = new Map<string, EncoderJob>();

/**
 * An local Adobe Media Encoder job that we've either obtained feedback from,
 * we're waiting for, or both.
 */
class EncoderJob {
  // The latest information we know about the job.
  lastEvent?: EncoderJobEvent;

  // How to signal the conclusion of the job.
  promiseSource?: PromiseSource<string>;

  // The encoder progress reporter
  ameProgressHandler?: (progress: number) => void;

  dispatchJobConclusion() {
    if (this.lastEvent && this.lastEvent.progress && this.ameProgressHandler) {
      this.ameProgressHandler(this.lastEvent.progress);
      return;
    }

    if (this.promiseSource == null || this.lastEvent == null)
      return;

    if (this.lastEvent.canceled) {
      lastEncoderEventByJobId.delete(this.lastEvent.jobId);
      this.promiseSource.deferred.reject(new Axios.Cancel('Encoder job cancelled'));
      return;
    }

    if (this.lastEvent.errorMessage) {
      lastEncoderEventByJobId.delete(this.lastEvent.jobId);
      this.promiseSource.deferred.reject(new Error(this.lastEvent.errorMessage));
      return;
    }

    if (this.lastEvent.outputFilePath) {
      lastEncoderEventByJobId.delete(this.lastEvent.jobId);
      this.promiseSource.deferred.resolve(this.lastEvent.outputFilePath);
    }
  }


  processEvent(jobEvent: EncoderJobEvent) {
    this.lastEvent = jobEvent;
    this.dispatchJobConclusion();
  }


  getExportedFile(): Promise<string> {
    if (!this.promiseSource) {
      this.promiseSource = defer<string>();
      this.dispatchJobConclusion();
    }
    return this.promiseSource.promise;
  }
}


